import {
  DestroyRef,
  Directive,
  ElementRef,
  Input,
  OnInit,
  inject,
} from '@angular/core';

/**
 * Attaches a drag-grip resize handle to a PrimeNG `<p-drawer>`.
 *
 * PrimeNG's Drawer has no built-in resize affordance. This directive
 * observes the host's DOM subtree for the `.p-drawer` element (it's
 * rendered lazily when the drawer first becomes visible), injects a
 * `.drawer-resize-handle` on the inward-facing edge, and wires up
 * pointer events to drive the drawer's inline width.
 *
 * Usage:
 *   <p-drawer position="right" libDrawerResize
 *             drawerResizeKey="candidate-drawer"
 *             [drawerResizeMin]="360"
 *             [drawerResizeMax]="1200">
 *
 * The width is persisted per `drawerResizeKey` in `localStorage` so
 * the user's choice sticks across sessions. Without a key the width
 * still resizes but doesn't persist. Constraints are enforced on
 * both live drag and restore.
 *
 * Only handles `position="right"` and `position="left"` drawers today
 * — top/bottom would need a vertical grip and a different clamp; wire
 * those up when a caller needs them.
 */
@Directive({
  selector: '[libDrawerResize]',
  standalone: true,
})
export class DrawerResizeDirective implements OnInit {
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Storage key for persisting the user's chosen width. When absent the
   *  drawer still resizes but the choice is per-session. */
  @Input() drawerResizeKey?: string;

  /** Minimum width in px. Below this the handle stops dragging. */
  @Input() drawerResizeMin = 320;

  /** Maximum width in px. Defaults to 90% of viewport width, computed
   *  on demand so a browser resize doesn't strand the drawer larger
   *  than the window. */
  @Input() drawerResizeMax?: number;

  /** Cached reference so we don't re-attach on every mutation. */
  private drawerEl: HTMLElement | null = null;
  private observer?: MutationObserver;
  private bodyObserver?: MutationObserver;

  /** CSS class the owning `<p-drawer>` carries (its PrimeNG `styleClass`).
   *  Used to disambiguate when the drawer is portaled to document.body
   *  via `appendTo="body"` and the body contains multiple drawers at the
   *  same time (e.g. candidate drawer + add-task drawer on top). By
   *  convention this matches `drawerResizeKey`; override only if the two
   *  values diverge. */
  private get handleOwnerClass(): string { return this.drawerResizeKey ?? ''; }

  ngOnInit(): void {
    // The .p-drawer element is stamped lazily by PrimeNG when the
    // drawer first opens. Watch the host subtree AND document.body
    // for it — hosts that render the drawer inline keep it inside the
    // component tree, but hosts that use appendTo="body" portal it
    // out, so looking only in `host` would miss those cases entirely.
    const attach = () => {
      const found =
        this.host.nativeElement.querySelector<HTMLElement>(`.p-drawer.${this.handleOwnerClass}`) ??
        document.body.querySelector<HTMLElement>(`.p-drawer.${this.handleOwnerClass}`) ??
        // Fallback for drawers without a stable styleClass — still works
        // as long as the directive host is the only p-drawer around.
        this.host.nativeElement.querySelector<HTMLElement>('.p-drawer') ??
        document.body.querySelector<HTMLElement>('.p-drawer');
      if (!found || found === this.drawerEl) return;
      this.drawerEl = found;
      this.attachHandle(found);
    };

    // Initial sweep in case the drawer is already open.
    attach();

    this.observer = new MutationObserver(attach);
    this.observer.observe(this.host.nativeElement, {
      childList: true,
      subtree:   true,
    });
    // Separate observer on document.body for the appendTo="body" case —
    // the drawer is portaled outside the host subtree so the first
    // observer never sees it. Scope the watch to body childList only
    // (plus subtree) to keep the callback firing cheap.
    this.bodyObserver = new MutationObserver(attach);
    this.bodyObserver.observe(document.body, {
      childList: true,
      subtree:   true,
    });

    this.destroyRef.onDestroy(() => {
      this.observer?.disconnect();
      this.observer = undefined;
      this.bodyObserver?.disconnect();
      this.bodyObserver = undefined;
    });
  }

  private attachHandle(drawer: HTMLElement): void {
    // Don't double-attach if PrimeNG re-uses the element across
    // opens (safety net).
    if (drawer.querySelector(':scope > .drawer-resize-handle')) {
      this.restoreWidth(drawer);
      return;
    }

    const handle = document.createElement('div');
    handle.className    = 'drawer-resize-handle';
    handle.tabIndex     = 0;
    handle.role         = 'separator';
    handle.setAttribute('aria-orientation', 'vertical');
    handle.setAttribute('aria-label', 'Resize drawer');
    // 6-dot grip matching osstre-prototyping-ui's Material
    // drag_indicator icon (two columns of 3 dots). Inline SVG so we
    // don't need to pull in the Material Icons webfont just for
    // this — the icon is trivially small and the style is stable.
    handle.innerHTML = `
      <svg class="drawer-resize-grip" viewBox="0 0 24 24" width="18" height="18"
           aria-hidden="true" focusable="false">
        <circle cx="9"  cy="5"  r="1.6"/>
        <circle cx="15" cy="5"  r="1.6"/>
        <circle cx="9"  cy="12" r="1.6"/>
        <circle cx="15" cy="12" r="1.6"/>
        <circle cx="9"  cy="19" r="1.6"/>
        <circle cx="15" cy="19" r="1.6"/>
      </svg>
    `;

    // The handle sits on the INWARD edge — leftmost for a right-
    // positioned drawer, rightmost for a left-positioned one. We
    // decide from the drawer's position class rather than the input
    // so a caller can't get them out of sync.
    const isLeftDrawer = drawer.classList.contains('p-drawer-left');
    handle.classList.add(isLeftDrawer ? 'drawer-resize-handle--right' : 'drawer-resize-handle--left');
    drawer.classList.add('drawer-resizable');
    drawer.appendChild(handle);

    this.restoreWidth(drawer);
    this.wireDrag(drawer, handle, isLeftDrawer);
  }

  private wireDrag(drawer: HTMLElement, handle: HTMLElement, isLeftDrawer: boolean): void {
    const onPointerDown = (down: PointerEvent) => {
      // Ignore secondary buttons — a right-click on the grip shouldn't
      // start a drag.
      if (down.button !== 0) return;
      down.preventDefault();
      handle.setPointerCapture(down.pointerId);
      document.body.classList.add('drawer-resizing');

      const startX     = down.clientX;
      const startWidth = drawer.getBoundingClientRect().width;

      const onMove = (move: PointerEvent) => {
        // Right-positioned drawer grows LEFT as the pointer moves left,
        // so delta is inverted. Left-positioned drawer grows to the
        // right, so delta is the raw pointer delta.
        const deltaX = move.clientX - startX;
        const raw    = startWidth + (isLeftDrawer ? deltaX : -deltaX);
        const max    = this.drawerResizeMax ?? Math.floor(window.innerWidth * 0.9);
        const width  = Math.max(this.drawerResizeMin, Math.min(raw, max));
        this.setDrawerWidth(drawer, width);
      };

      const onUp = () => {
        handle.releasePointerCapture(down.pointerId);
        document.body.classList.remove('drawer-resizing');
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup',   onUp);
        handle.removeEventListener('pointercancel', onUp);
        this.persistWidth(drawer);
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup',   onUp);
      handle.addEventListener('pointercancel', onUp);
    };

    handle.addEventListener('pointerdown', onPointerDown);

    // Keyboard: left/right arrows nudge width by 24px so keyboard-only
    // users can still resize. Same clamp as the drag path.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const nudge = e.key === 'ArrowLeft' ? -24 : 24;
      const raw   = drawer.getBoundingClientRect().width + (isLeftDrawer ? nudge : -nudge);
      const max   = this.drawerResizeMax ?? Math.floor(window.innerWidth * 0.9);
      const width = Math.max(this.drawerResizeMin, Math.min(raw, max));
      this.setDrawerWidth(drawer, width);
      this.persistWidth(drawer);
    };
    handle.addEventListener('keydown', onKeyDown);

    this.destroyRef.onDestroy(() => {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('keydown',     onKeyDown);
    });
  }

  private restoreWidth(drawer: HTMLElement): void {
    if (!this.drawerResizeKey) return;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (raw === null) return;
      const width = Number(raw);
      if (!Number.isFinite(width) || width <= 0) return;
      const max = this.drawerResizeMax ?? Math.floor(window.innerWidth * 0.9);
      const clamped = Math.max(this.drawerResizeMin, Math.min(width, max));
      this.setDrawerWidth(drawer, clamped);
    } catch {
      // Private mode / storage disabled — degrade to the drawer's
      // theme-default width silently.
    }
  }

  private persistWidth(drawer: HTMLElement): void {
    if (!this.drawerResizeKey) return;
    try {
      const width = drawer.getBoundingClientRect().width;
      localStorage.setItem(this.storageKey, String(Math.round(width)));
    } catch {
      // See restoreWidth — degrade silently.
    }
  }

  private get storageKey(): string {
    return `kaboom.drawer-width.${this.drawerResizeKey}`;
  }

  /**
   * Write width to the drawer element WITH `!important` priority.
   * The per-drawer styleClass rules (e.g. `.job-drawer { width:
   * 500px !important }` in feature-jobs.scss) use !important to
   * pin the drawer's default size against PrimeNG's own base rules —
   * a plain `.style.width = ...` assignment doesn't beat those.
   * Using CSSStyleDeclaration.setProperty with the priority argument
   * ensures the inline style wins the cascade for every drawer in
   * the app without having to strip `!important` from every
   * per-feature stylesheet.
   */
  private setDrawerWidth(drawer: HTMLElement, width: number): void {
    drawer.style.setProperty('width', `${Math.round(width)}px`, 'important');
    // max-width follows the width so an inline value larger than the
    // per-drawer max-width (also often !important) still renders.
    drawer.style.setProperty('max-width', `${Math.round(width)}px`, 'important');
  }
}
